/**
 * Authentication Utilities
 *
 * Token and user storage. The backend issues a JWT without expiry and
 * keeps it server-side too; there is no refresh flow. A session ends on
 * logout or when the API answers 401 (token invalidated elsewhere).
 */

import { AUTH_CONFIG, ROLE_NAMES_BY_ID } from '@/config/constants';
import type { ApiUser } from '@/types';
import { getImpersonation, stopImpersonation } from '@/utils/impersonation';

/**
 * Decoded JWT payload
 */
export interface DecodedJWT {
  exp?: number;
  iat?: number;
  email?: string;
  user_id?: number;
  [key: string]: any;
}

/**
 * Decode a JWT token without verification (returns null if invalid).
 * Note: the backend login token carries no exp claim.
 */
export const decodeJWT = (accessToken: string): DecodedJWT | null => {
  try {
    if (!accessToken) return null;
    const parts = accessToken.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Store the token and user record after a successful login
 */
export const storeAuthData = (token: string, user: ApiUser): void => {
  localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
  localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user));
  localStorage.setItem(AUTH_CONFIG.ROLE_KEY, resolveRoleName(user));
};

/**
 * Store the plan module ids that unlock features for a non-admin tenant.
 * Persisted separately so feature gating can be read synchronously.
 */
export const storePlanModules = (modules: number[]): void => {
  localStorage.setItem(AUTH_CONFIG.PLAN_MODULES_KEY, JSON.stringify(modules));
};

/**
 * Clear all authentication data
 */
export const clearAuthData = (): void => {
  stopImpersonation();
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.USER_KEY);
  localStorage.removeItem(AUTH_CONFIG.ROLE_KEY);
  localStorage.removeItem(AUTH_CONFIG.PLAN_MODULES_KEY);
};

/**
 * Get stored access token
 */
export const getToken = (): string | null => {
  return localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
};

/**
 * Get current user record from localStorage
 */
export const getCurrentUser = (): ApiUser | null => {
  const userJson = localStorage.getItem(AUTH_CONFIG.USER_KEY);
  if (!userJson) return null;

  try {
    return JSON.parse(userJson) as ApiUser;
  } catch {
    return null;
  }
};

/**
 * Resolve the role name of a user record ('admin', 'organization',
 * 'staff', 'client', 'organization-manager')
 */
export const resolveRoleName = (user: ApiUser): string => {
  return user['role.role'] || ROLE_NAMES_BY_ID[user.role_id] || '';
};

/**
 * Get current user role name. While impersonating (see utils/impersonation.ts)
 * this is the TARGET's role — every role-derived decision in the app (menus,
 * guards, permissions) must follow the impersonated profile, exactly like the
 * API does per request. The real role stays stored untouched and comes back
 * the moment impersonation stops.
 */
export const getCurrentRole = (): string | null => {
  const impersonation = getImpersonation();
  if (impersonation) return impersonation.role;
  return localStorage.getItem(AUTH_CONFIG.ROLE_KEY);
};

/**
 * Numeric role id of the current user (null if unknown). While impersonating,
 * this is the TARGET's role_id — see getCurrentRole().
 */
export const getRoleId = (): number | null => {
  const impersonation = getImpersonation();
  if (impersonation) return impersonation.roleId;
  const user = getCurrentUser();
  return user && typeof user.role_id === 'number' ? user.role_id : null;
};

/**
 * user_id of the current user (null if unknown).
 */
export const getCurrentUserId = (): number | null => {
  const user = getCurrentUser();
  return user && typeof user.user_id === 'number' ? user.user_id : null;
};

/**
 * The organization id the current user belongs to (null for admins or when
 * the record carries no organization; managers/staff may map to several via
 * the backend, this is only the primary hint on the user record).
 */
export const getOrganizationId = (): number | null => {
  const user = getCurrentUser();
  return user && typeof user.organization_id === 'number' ? user.organization_id : null;
};

/**
 * The client id that owns the current user's organizations (role 4 owns
 * organizations via users.client_id). Null for admins.
 */
export const getClientId = (): number | null => {
  const user = getCurrentUser();
  return user && typeof user.client_id === 'number' ? user.client_id : null;
};

/**
 * Plan module ids unlocking features for the current tenant ([] if none).
 * While impersonating, this is the TARGET's plan modules — see getCurrentRole().
 */
export const getPlanModules = (): number[] => {
  const impersonation = getImpersonation();
  if (impersonation) return impersonation.planModules;
  const raw = localStorage.getItem(AUTH_CONFIG.PLAN_MODULES_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((m) => Number(m)).filter((m) => !Number.isNaN(m)) : [];
  } catch {
    return [];
  }
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (requiredRoles: string[]): boolean => {
  const currentRole = getCurrentRole();
  return currentRole ? requiredRoles.includes(currentRole) : false;
};
